import React, { useState, useEffect } from 'react';
import { FileText, DollarSign, RefreshCw, Sparkles, Edit3, Copy, MoreHorizontal } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

interface AIContentGeneratorContentProps {
  animationDelay?: number;
}

const contentTabs = ['All', 'Social', 'Blog', 'Email', 'Website'];

const contentItems = [
  { title: 'Instagram post about summer sale...', date: 'Jan 15', variations: 3, words: 245, type: 'Social' },
  { title: 'Email newsletter for Q1 launch...', date: 'Jan 14', variations: 2, words: 512, type: 'Email' },
  { title: 'Blog intro for SEO strategy...', date: 'Jan 13', variations: 4, words: 1024, type: 'Blog' },
];

const AIContentGeneratorContent: React.FC<AIContentGeneratorContentProps> = ({ animationDelay = 0 }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [totalWords, setTotalWords] = useState(0);
  const [cost, setCost] = useState(0);
  const [wordsPerDay, setWordsPerDay] = useState(0);
  const [typingText, setTypingText] = useState('');
  const [showNewRow, setShowNewRow] = useState(false);

  const fullText = 'Generating engaging content...';

  useEffect(() => {
    // Counter animations
    const wordsInterval = setInterval(() => {
      setTotalWords(prev => {
        if (prev >= 12847) return 12847;
        return prev + Math.ceil((12847 - prev) / 10);
      });
    }, 40);

    const costInterval = setInterval(() => {
      setCost(prev => {
        if (prev >= 24) return 24;
        return prev + 1;
      });
    }, 80);

    const dailyInterval = setInterval(() => {
      setWordsPerDay(prev => {
        if (prev >= 458) return 458;
        return prev + Math.ceil((458 - prev) / 8);
      });
    }, 50);

    // Typing animation
    let charIndex = 0;
    const typingTimer = setTimeout(() => {
      const typingInterval = setInterval(() => {
        if (charIndex < fullText.length) {
          setTypingText(fullText.slice(0, charIndex + 1));
          charIndex++;
        } else {
          clearInterval(typingInterval);
          setTimeout(() => setShowNewRow(true), 500);
        }
      }, 60);
    }, animationDelay + 1500);

    return () => {
      clearInterval(wordsInterval);
      clearInterval(costInterval);
      clearInterval(dailyInterval);
      clearTimeout(typingTimer);
    };
  }, [animationDelay]);

  return (
    <div 
      className="flex-1 flex flex-col overflow-hidden"
      style={{ 
        backgroundColor: isDark ? '#1A1A1A' : '#F5F5F5',
        animation: `fadeIn 0.4s ease-out ${animationDelay}ms both`
      }}
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between px-3 py-2 border-b"
        style={{ 
          borderColor: isDark ? '#333333' : '#E5E5E5',
          backgroundColor: isDark ? '#222222' : '#FFFFFF',
          animation: `fadeIn 0.3s ease-out ${animationDelay + 100}ms both`
        }}
      >
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>
            Content AI
          </span>
          <span 
            className="text-[7px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5"
            style={{ 
              backgroundColor: '#8B5CF620',
              color: '#8B5CF6'
            }}
          >
            <Sparkles className="w-2 h-2" />
            Pro
          </span>
        </div>
        <div 
          className="flex items-center gap-0.5 text-[7px] px-1.5 py-0.5 rounded"
          style={{ 
            backgroundColor: isDark ? '#10B98120' : '#10B98110',
            color: '#10B981'
          }}
        >
          <Edit3 className="w-2 h-2" />
          Generate
        </div>
      </div>

      {/* Stats Row */}
      <div 
        className="grid grid-cols-3 gap-1.5 p-2"
        style={{ animation: `fadeIn 0.4s ease-out ${animationDelay + 200}ms both` }}
      >
        <div 
          className="rounded-lg p-2 flex items-center gap-2"
          style={{ 
            backgroundColor: isDark ? '#222222' : '#FFFFFF',
            border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`,
            animation: `scaleIn 0.3s ease-out ${animationDelay + 300}ms both`
          }}
        >
          <div className="w-5 h-5 rounded-full bg-[#3B82F620] flex items-center justify-center">
            <FileText className="w-2.5 h-2.5" style={{ color: '#3B82F6' }} />
          </div>
          <div>
            <div className="text-[6px]" style={{ color: isDark ? '#888888' : '#666666' }}>Total words</div>
            <div className="text-[10px] font-bold" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>
              {totalWords.toLocaleString()}
            </div>
          </div>
        </div>

        <div 
          className="rounded-lg p-2 flex items-center gap-2"
          style={{ 
            backgroundColor: isDark ? '#222222' : '#FFFFFF',
            border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`,
            animation: `scaleIn 0.3s ease-out ${animationDelay + 400}ms both`
          }}
        >
          <div className="w-5 h-5 rounded-full bg-[#10B98120] flex items-center justify-center">
            <DollarSign className="w-2.5 h-2.5" style={{ color: '#10B981' }} />
          </div>
          <div>
            <div className="text-[6px]" style={{ color: isDark ? '#888888' : '#666666' }}>Cost saved</div>
            <div className="text-[10px] font-bold" style={{ color: '#10B981' }}>
              ${cost}
            </div>
          </div>
        </div>

        <div 
          className="rounded-lg p-2 flex items-center gap-2"
          style={{ 
            backgroundColor: isDark ? '#222222' : '#FFFFFF',
            border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`,
            animation: `scaleIn 0.3s ease-out ${animationDelay + 500}ms both`
          }}
        >
          <div className="w-5 h-5 rounded-full bg-[#F59E0B20] flex items-center justify-center">
            <RefreshCw className="w-2.5 h-2.5" style={{ color: '#F59E0B' }} />
          </div>
          <div>
            <div className="text-[6px]" style={{ color: isDark ? '#888888' : '#666666' }}>Words/day</div>
            <div className="text-[10px] font-bold" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>
              {wordsPerDay}
            </div>
          </div>
        </div>
      </div>

      {/* Content Tabs */}
      <div 
        className="flex gap-1 px-2"
        style={{ animation: `fadeIn 0.3s ease-out ${animationDelay + 600}ms both` }}
      >
        {contentTabs.map((tab, i) => (
          <span 
            key={tab}
            className="text-[7px] px-1.5 py-0.5 rounded"
            style={{ 
              backgroundColor: i === 0 ? (isDark ? '#3B82F620' : '#3B82F610') : 'transparent',
              color: i === 0 ? '#3B82F6' : (isDark ? '#888888' : '#666666')
            }}
          >
            {tab}
          </span>
        ))}
      </div>

      {/* Content Table */}
      <div 
        className="flex-1 mx-2 my-2 rounded-lg overflow-hidden"
        style={{ 
          backgroundColor: isDark ? '#222222' : '#FFFFFF',
          border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`,
          animation: `fadeIn 0.4s ease-out ${animationDelay + 700}ms both`
        }}
      >
        {/* Table Header */}
        <div 
          className="grid grid-cols-12 gap-1 px-2 py-1.5 border-b text-[6px]"
          style={{ 
            borderColor: isDark ? '#333333' : '#E5E5E5',
            color: isDark ? '#888888' : '#666666'
          }}
        >
          <span className="col-span-5">Content</span>
          <span className="col-span-2">Date</span>
          <span className="col-span-2">Vars</span>
          <span className="col-span-2">Words</span>
          <span className="col-span-1"></span>
        </div>

        {/* Table Rows */}
        <div className="divide-y" style={{ borderColor: isDark ? '#2A2A2A' : '#F0F0F0' }}>
          {contentItems.map((item, i) => (
            <div 
              key={i}
              className="grid grid-cols-12 gap-1 px-2 py-1.5 items-center"
              style={{ 
                animation: `slideInFromBottom 0.3s ease-out ${animationDelay + 800 + (i * 100)}ms both`
              }}
            >
              <div className="col-span-5 flex items-center gap-1">
                <Sparkles className="w-2 h-2 flex-shrink-0" style={{ color: '#8B5CF6' }} />
                <span 
                  className="text-[7px] truncate"
                  style={{ color: isDark ? '#FFFFFF' : '#111111' }}
                >
                  {item.title}
                </span>
              </div>
              <span className="col-span-2 text-[6px]" style={{ color: isDark ? '#888888' : '#666666' }}>
                {item.date}
              </span>
              <span 
                className="col-span-2 text-[6px] px-1 py-0.5 rounded w-fit"
                style={{ 
                  backgroundColor: '#8B5CF620',
                  color: '#8B5CF6'
                }}
              >
                {item.variations} vars
              </span>
              <span className="col-span-2 text-[6px]" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>
                {item.words.toLocaleString()}
              </span>
              <div className="col-span-1 flex justify-end">
                <MoreHorizontal className="w-2.5 h-2.5" style={{ color: isDark ? '#666666' : '#999999' }} />
              </div>
            </div>
          ))}

          {/* Typing Row */}
          {typingText && (
            <div 
              className="grid grid-cols-12 gap-1 px-2 py-1.5 items-center"
              style={{ 
                backgroundColor: isDark ? '#8B5CF610' : '#8B5CF608',
              }}
            >
              <div className="col-span-5 flex items-center gap-1">
                <Sparkles 
                  className="w-2 h-2 flex-shrink-0" 
                  style={{ 
                    color: '#8B5CF6',
                    animation: 'sparkle 0.8s ease-in-out infinite'
                  }} 
                />
                <span className="text-[7px]" style={{ color: '#8B5CF6' }}>
                  {typingText}
                  <span style={{ animation: 'blink 0.5s ease-in-out infinite' }}>|</span>
                </span>
              </div>
              <span className="col-span-2 text-[6px]" style={{ color: isDark ? '#888888' : '#666666' }}>
                Now
              </span>
              <span className="col-span-2 text-[6px]" style={{ color: isDark ? '#888888' : '#666666' }}>
                —
              </span>
              <span className="col-span-2 text-[6px]" style={{ color: isDark ? '#888888' : '#666666' }}>
                ...
              </span>
              <div className="col-span-1" />
            </div>
          )}

          {/* New Row Animation */}
          {showNewRow && (
            <div 
              className="grid grid-cols-12 gap-1 px-2 py-1.5 items-center"
              style={{ 
                animation: 'slideInFromBottom 0.4s ease-out both'
              }}
            >
              <div className="col-span-5 flex items-center gap-1">
                <Sparkles className="w-2 h-2 flex-shrink-0" style={{ color: '#10B981' }} />
                <span className="text-[7px]" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>
                  Product launch announcement...
                </span>
              </div>
              <span className="col-span-2 text-[6px]" style={{ color: isDark ? '#888888' : '#666666' }}>
                Just now
              </span>
              <span 
                className="col-span-2 text-[6px] px-1 py-0.5 rounded w-fit"
                style={{ backgroundColor: '#10B98120', color: '#10B981' }}
              >
                ✓ Done
              </span>
              <span className="col-span-2 text-[6px]" style={{ color: '#10B981' }}>
                387
              </span>
              <div className="col-span-1 flex justify-end">
                <Copy className="w-2.5 h-2.5" style={{ color: '#10B981' }} />
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideInFromBottom {
          0% { transform: translateY(10px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        @keyframes sparkle {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.2); opacity: 0.8; }
        }
      `}</style>
    </div>
  );
};

export default AIContentGeneratorContent;
